import {
    formatCustomValue,
    formatMinor,
    getBillToCustomValue,
    getCustomValue,
    invoiceNotes,
    visibleBillToFields,
    visibleColumns,
} from '../helpers';
import {
    Invoice,
    InvoiceBuilderColumn,
    InvoiceBuilderCustomColumn,
    InvoiceLine,
} from '../types';

export const PRESTIGE_INVOICE_AGENT_IDS = [
    'TqPzkkMHGAhYjWrJ9neo',
    'WbrA7WhIweqRXl6WK3aJ',
] as const;

export const PRESTIGE_BODY_ROWS = 18;

export const isPrestigeInvoiceAgent = (agentId?: string | null) => (
    typeof agentId === 'string'
    && (PRESTIGE_INVOICE_AGENT_IDS as readonly string[]).includes(agentId)
);

const normalizeLabel = (label: string) => (
    label
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
);

type FieldSlot = 'nombre' | 'telefono' | 'auto' | 'cliente' | 'ci' | 'cuba' | 'vin' | 'color';

const LABEL_SLOTS: Record<string, FieldSlot> = {
    NOMBRE: 'nombre',
    NAME: 'nombre',
    TELEFONO: 'telefono',
    TEL: 'telefono',
    PHONE: 'telefono',
    AUTO: 'auto',
    VEHICULO: 'auto',
    VEHICLE: 'auto',
    CLIENTE: 'cliente',
    CLIENT: 'cliente',
    CI: 'ci',
    CUBA: 'cuba',
    VIN: 'vin',
    COLOR: 'color',
    COLOUR: 'color',
};

const FIELD_GRID: Array<Array<FieldSlot | null>> = [
    ['nombre', 'ci', null],
    ['telefono', 'cuba', null],
    ['auto', 'vin', 'color'],
    ['cliente', null, null],
];

export interface PrestigeField {
    label: string;
    value: string;
}

export interface PrestigeColumn {
    key: string;
    label: string;
    align: 'center' | 'right';
    flex: boolean;
    width: number;
}

export interface PrestigeLineRow {
    key: string;
    cells: string[];
}

export interface PrestigeTotalRow {
    key: string;
    label: string;
    value: string;
}

export interface PrestigeInvoiceModel {
    title: string;
    dateLabel: string;
    issuedOn: string;
    billToLabel: string;
    logoImageUrl: string;
    wordmark: string;
    sellerLines: string[];
    billToRows: Array<Array<PrestigeField | null>>;
    columns: PrestigeColumn[];
    showColumnSpacer: boolean;
    rows: PrestigeLineRow[];
    currencySymbol: string;
    summary: PrestigeTotalRow[];
    total: string;
    footerImages: { id: string; imageUrl: string; size?: Invoice['document']['design']['logo']['size'] }[];
    footerLines: string[];
    note: string;
    isVoid: boolean;
}

const markedTaxed = (value: string) => /^(true|yes|y|x|1|si)$/i.test(value.trim());

export const prestigeDate = (isoDate: string) => {
    const match = String(isoDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';
    return `${match[3]}/${match[2]}/${match[1].slice(2)}`;
};

export const prestigeAmount = (
    amountMinor: number,
    currency: string,
    locale: string,
) => {
    if (!amountMinor) return '-';
    const formatted = formatMinor(Math.abs(amountMinor), currency, locale);
    const number = formatted.replace(/^[^\d]+/, '').trim();
    if (!number) return '-';
    return amountMinor < 0 ? `-${number}` : number;
};

const sellerLines = (seller: Invoice['document']['design']['seller']) => {
    const address = seller.address || {};
    const contact = seller.contact || { email: '', phone: '', website: '' };
    const place = [address.region, address.postalCode]
        .map((part) => (part || '').trim())
        .filter(Boolean)
        .join(' ');
    const direccion = [address.line1, address.line2, address.city, place]
        .map((part) => (part || '').trim())
        .filter(Boolean)
        .join(' , ');
    const email = (contact.email || '').trim();
    const phone = (contact.phone || '').trim();
    return [
        direccion ? `DIRECCION: ${direccion.toUpperCase()}` : '',
        email ? `CORREO: ${email.toUpperCase()}` : '',
        phone ? `TELEFONO: ${phone}` : '',
    ].filter(Boolean);
};

const currencySymbol = (currency: string, locale: string) => {
    const formatted = formatMinor(0, currency, locale);
    const symbol = formatted.match(/^[^\d-]+/)?.[0]?.trim();
    return symbol || '$';
};

const fieldEntry = (
    label: string,
    value: string,
): PrestigeField => ({
    label: label.trim().toUpperCase(),
    value: value.trim().toUpperCase(),
});

const customerFieldSet = (
    fields: Invoice['document']['design']['customerFields'],
) => new Set(fields?.includes('name') ? fields : []);

const pairedRows = (fields: PrestigeField[]) => {
    const rows: Array<Array<PrestigeField | null>> = [];
    for (let index = 0; index < fields.length; index += 2) {
        rows.push([fields[index] || null, fields[index + 1] || null]);
    }
    return rows;
};

const billToGrid = (invoice: Invoice) => {
    const { document: doc } = invoice;
    const slots = new Map<FieldSlot, PrestigeField>();
    const ordered: PrestigeField[] = [];
    const extras: PrestigeField[] = [];

    visibleBillToFields(doc.design.billToFields).forEach((field) => {
        const entry = fieldEntry(
            field.label,
            formatCustomValue(
                getBillToCustomValue(doc.billToCustomValues, field.id),
                field.valueKind,
            ),
        );
        ordered.push(entry);
        const slot = LABEL_SLOTS[normalizeLabel(field.label)];
        if (slot && !slots.has(slot)) {
            slots.set(slot, entry);
            return;
        }
        extras.push(entry);
    });

    const customerFields = customerFieldSet(doc.design.customerFields);
    if (!slots.has('nombre') && customerFields.has('name') && doc.buyer.name.trim()) {
        const entry = fieldEntry('NOMBRE', doc.buyer.name);
        slots.set('nombre', entry);
        ordered.unshift(entry);
    }
    if (!slots.has('telefono') && customerFields.has('phone') && doc.buyer.phone.trim()) {
        const entry = fieldEntry('TELEFONO', doc.buyer.phone);
        slots.set('telefono', entry);
        ordered.splice(slots.has('nombre') ? 1 : 0, 0, entry);
    }

    if (extras.length) return pairedRows(ordered);

    const rows = FIELD_GRID.map((row) => row.map((slot) => (
        slot ? slots.get(slot) || null : null
    )));
    return rows.filter((row) => row.some(Boolean));
};

const columnModel = (column: InvoiceBuilderColumn): PrestigeColumn => {
    if (column.kind === 'description') {
        return {
            key: column.id,
            label: column.label.trim().toUpperCase() || 'DESCRIPTION',
            align: 'center',
            flex: true,
            width: 0,
        };
    }
    if (column.kind === 'amount') {
        return {
            key: column.id,
            label: column.label.trim().toUpperCase() || 'AMOUNT',
            align: 'right',
            flex: false,
            width: 72,
        };
    }
    return {
        key: column.id,
        label: column.label.trim().toUpperCase(),
        align: 'center',
        flex: false,
        width: column.kind === 'quantity' || column.kind === 'rate' ? 48 : 54,
    };
};

const lineCell = (
    column: InvoiceBuilderColumn,
    line: InvoiceLine,
    currency: string,
    locale: string,
) => {
    if (column.kind === 'description') return line.description;
    if (column.kind === 'quantity') return line.quantity;
    if (column.kind === 'rate' || column.kind === 'amount') {
        const minor = column.kind === 'rate' ? line.rateMinor : line.amounts.totalMinor;
        if (!minor) return '';
        return prestigeAmount(minor, currency, locale);
    }
    if (column.kind !== 'custom') return '';
    const raw = getCustomValue(line.customValues, column.id);
    if ((column as InvoiceBuilderCustomColumn).valueKind === 'boolean') {
        return markedTaxed(raw) ? 'X' : '';
    }
    return formatCustomValue(raw, column.valueKind);
};

export const getPrestigeInvoiceModel = (invoice: Invoice): PrestigeInvoiceModel => {
    const { document: doc } = invoice;
    const { design } = doc;
    const columns = visibleColumns(design.columns);
    const locale = design.regional.locale || 'en-US';
    const currency = doc.totals.currency || design.regional.currency || 'USD';
    const sourceColumns: InvoiceBuilderColumn[] = columns.length > 0 ? columns : [{
        id: 'description',
        kind: 'description',
        label: 'Description',
        isVisible: true,
        order: 0,
    }];
    const tableColumns = sourceColumns.map(columnModel);
    const dataRows = doc.lines.map((line) => ({
        key: line.id,
        cells: sourceColumns.map((column) => lineCell(column, line, currency, locale)),
    }));
    const rows = dataRows;
    while (rows.length < PRESTIGE_BODY_ROWS) {
        rows.push({
            key: `blank-${rows.length}`,
            cells: tableColumns.map(() => ''),
        });
    }

    const fees = doc.totals.fees || [];
    const summary: PrestigeTotalRow[] = [
        {
            key: 'subtotal',
            label: 'Subtotal',
            value: prestigeAmount(doc.totals.subtotalMinor, currency, locale),
        },
        ...fees.map((fee) => ({
            key: fee.id,
            label: fee.label,
            value: prestigeAmount(fee.amountMinor, currency, locale),
        })),
    ];

    const footerBlocks = [...(design.footerBlocks || [])]
        .sort((left, right) => left.order - right.order);

    return {
        title: (design.labels.documentTitle || 'FACTURA').trim().toUpperCase(),
        dateLabel: 'DATE',
        issuedOn: prestigeDate(invoice.issuedOn),
        billToLabel: (design.labels.billTo || 'BILL TO').trim().toUpperCase(),
        logoImageUrl: design.logo.style === 'image' ? design.logo.imageUrl : '',
        wordmark: design.logo.style === 'wordmark' ? design.logo.text : '',
        sellerLines: sellerLines(design.seller),
        billToRows: billToGrid(invoice),
        columns: tableColumns,
        showColumnSpacer: tableColumns.filter((column) => !column.flex).length === 2,
        rows,
        currencySymbol: currencySymbol(currency, locale),
        summary,
        total: prestigeAmount(doc.totals.totalMinor, currency, locale),
        footerImages: footerBlocks.flatMap((block) => (
            block.kind === 'image' && block.imageUrl
                ? [{ id: block.id, imageUrl: block.imageUrl, size: block.size }]
                : []
        )),
        footerLines: footerBlocks.flatMap((block) => (
            block.kind === 'text' && block.markdown.trim() ? [block.markdown] : []
        )),
        note: invoiceNotes(invoice),
        isVoid: invoice.status === 'void',
    };
};
